import { ErrorHandler, Injectable } from '@angular/core';
import { ToastController } from '@ionic/angular';
import { FrontendConfiguration } from 'src/configuration/config.patientportal';
import { GraphQLNetworkError } from './GraphQLNetworkError';
import { HttpError } from './HttpError';

@Injectable({
    providedIn: 'root',
})
export class ErrorHandlingService extends ErrorHandler {
    constructor(private toastController: ToastController) {
        super();
    }

    configuration = new FrontendConfiguration();

    override handleError(error: Error | HttpError): void {
        // Call the default ErrorHandler which writes to console
        super.handleError(error);

        if (typeof error === 'object')
            if (typeof error === 'string') {
                // Additionally, display error in a snackbar to get some attention from a developer
                this.showNotification(`[DEBUG] Unhandled error: ${error}`);
                return;
            }

        if (typeof error === 'object' && 'code' in error && error.code == -100) {
            // Ignore NG0100: ExpressionChangedAfterItHasBeenCheckedError, because it brings no value - it's hard to diagnose, non critical, and we just learned to ignore it,
            // so the error message is just an annoyance
            return;
        }
        if (typeof error === 'object' && 'status' in error) {
            const httpError = error as HttpError;

            let message =
                // best message in case of unhandled exceptions in backend:
                httpError.error?.ExceptionMessage ??
                // best in case of `return BadRequest("message as string") in backend`:
                (typeof httpError.error === 'string' ? httpError.error : null) ??
                // best in case of ASP.NET Core built-in validation with DataAnnotations like [Required]`:
                this.TryExtractValidationMessages(httpError) ??
                // best in case of empty `return BadRequest() in backend`:
                httpError.error?.title ??
                // in other cases, `message` is usually not empty, although this would be typically be quite generic message
                httpError.message;

            this.showNotification(`[DEBUG] Unexpected ${httpError.status}: ${message}. Url: ${httpError.url}`);
        } else if (typeof error === 'object' && 'networkError' in error && error.networkError !== null) {
            // Unhandled Apollo (GraphQL) error
            const networkError = error.networkError as GraphQLNetworkError;
            let message = networkError.error?.errors[0]?.extensions?.message ?? networkError.error?.errors[0]?.extensions?.code ?? networkError.message;

            if (message !== 'errorMessage.account.notFound')
                this.showNotification(`[DEBUG] Unexpected ${networkError.status}: ${message}. Url: ${networkError.url}`);
        } else {
            this.showNotification(`[DEBUG] Unhandled error: ${error.message || 'undefined error'}`);
        }
    }

    // Extracts error messages from the ASP.NET Core validation response model, and transforms to a developer-readable format
    private TryExtractValidationMessages(error: HttpError): string | null {
        const extractedMessages: string[] = [];

        // I'm not sure why, but sometimes Errors for failed 400 request have response as object in `error` property, but sometimes only in `response` as JSON-string
        const objectWithValidationErrorsInAspNetFormat = error.error ?? (error.response !== undefined ? JSON.parse(error.response) : {});

        for (let key in objectWithValidationErrorsInAspNetFormat) {
            if (!Array.isArray(objectWithValidationErrorsInAspNetFormat[key])) return null;
            extractedMessages.push(...objectWithValidationErrorsInAspNetFormat[key]);
        }

        const joinedMessages = extractedMessages.join(', ');
        return joinedMessages;
    }

    async showNotification(message: string) {
        if (!this.configuration.production) {
            const toast = await this.toastController.create({
                message: message,
                position: 'bottom',
                color: 'medium',
                buttons: [
                    {
                        side: 'end',
                        text: 'Close',
                        role: 'cancel',
                    },
                ],
                duration: 0, // Keep visible until closed manually
            });
            await toast.present();
        }
    }
}
